import { http } from './client';
import type {
  AdminProfile,
  CapabilityGrant,
  CapabilityRequest,
  CountryOption,
  CurrencyOption,
  OrgOption,
  Organization,
  OrgRole,
  OrganizationSummary,
  OrgBranding,
  OrgBrandingAssetKind,
  OrgBrandingAssetUpload,
  OrgFeatures,
  Paged,
  Tenant,
  TenantConfigDraft,
  TenantDetail,
  UserDetail,
  UserMoveResult,
  UserRow,
  Workspace,
  WorkspaceMember,
  WorkspaceRole,
} from '../types';

/** Backoffice endpoints, grouped by resource. Paths are relative to /api/v1/backoffice. */

export const authApi = {
  // Google SSO is the only sign-in. There is no password endpoint to call: the
  // backend dropped POST /auth/login and the admins.password column with it,
  // because a password carries no domain and the domain is the whole gate.
  googleLogin: (idToken: string) =>
    http.post<{ access_token: string; refresh_token?: string; token_type?: string }>(
      '/auth/google',
      { id_token: idToken },
      { silent: true },
    ),
  logout: (refresh_token: string) => http.post<null>('/auth/logout', { refresh_token }),
  /**
   * There is no `/auth/me` on the backoffice API, so a stored token is checked
   * by calling something cheap that requires one. The admin list is gated on
   * being an active backoffice admin and nothing more, which is exactly the
   * question worth asking: a failure here means signed out, not under-
   * privileged — there is no role above "admin" left to lack.
   */
  probeSession: () =>
    http.get<{ items?: AdminProfile[] } | AdminProfile[]>('/admins', { per_page: 1 }, { silent: true }),
};

export const orgApi = {
  list: (params: { page?: number; per_page?: number }) =>
    http.get<Paged<Organization>>('/organizations', params),
  /**
   * `features` is the org's product scope. Omit it for an unrestricted org —
   * every page, every metric, every chart.
   */
  create: (body: {
    name: string;
    org_type: string;
    /**
     * The market this org's workspaces get when none is named — its default
     * workspace (D6) included, and every self-serve signup on its subdomain.
     * Required (AD-32): a workspace without a market can never have its KPIs
     * calculated, and signup has no way to ask for one.
     */
    default_country: string;
    tenant_id?: number | null;
    features?: Partial<OrgFeatures>;
    /** Markets this org's workspaces may use; omit for every selectable one. */
    allowed_countries?: string[] | null;
    /**
     * Console language new members start in. Omit for 'en-US'. Set it at
     * creation for a customer who reads the console in French, so their first
     * admin is provisioned in the right language rather than correcting it
     * afterwards.
     */
    default_language?: string;
  }) =>
    http.post<{ organization: OrganizationSummary; default_workspace: Workspace }>(
      '/organizations',
      body,
    ),
  createAdminUser: (
    orgId: number,
    body: {
      email: string;
      first_name: string;
      last_name: string;
      country_code?: string;
      phone_number?: string;
    },
  ) => http.post<Record<string, unknown>>(`/organizations/${orgId}/admin-users`, body),
  attachToTenant: (tenantId: number, org_id: number) =>
    http.post<OrganizationSummary>(`/organizations/tenants/${tenantId}/attach-org`, {
      org_id,
    }),
  /**
   * Replace this org's product scope.
   *
   * The whole object every time, because the console states it whole: a partial
   * update would let two operators editing the same org silently merge into a
   * third state neither chose. Takes effect on each member's next `/auth/me`;
   * export and PDF gating read the row directly, so those are immediate.
   */
  setFeatures: (orgId: number, features: OrgFeatures) =>
    http.put<{ org_id: number; features: OrgFeatures }>(
      `/organizations/${orgId}/features`,
      { features },
    ),
  /** Replace this org's branding images and logo flag (AD-141). https only. */
  setBranding: (orgId: number, branding: OrgBranding) =>
    http.put<{ org_id: number; branding: OrgBranding }>(
      `/organizations/${orgId}/branding`,
      { branding },
    ),
  /**
   * Stores a branding image in the public bucket and returns its URL. Saves
   * nothing: the console previews it, and `setBranding` applies it.
   *
   * The explicit multipart type stops axios turning the FormData into JSON
   * (the instance defaults to JSON); the browser then adds the boundary.
   */
  uploadBrandingAsset: (orgId: number, kind: OrgBrandingAssetKind, file: File) => {
    const body = new FormData();
    body.append('kind', kind);
    body.append('file', file);
    return http.post<OrgBrandingAssetUpload>(`/organizations/${orgId}/branding/assets`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /**
   * Replace this org's geo policy: which markets it may use, and which one by
   * default.
   *
   * Its own call rather than fields on `setFeatures`, because it is not product
   * scope: it decides which market the customer's inventory is measured
   * against, bounded by what the measurement API accepts. `allowed_countries`
   * null means every selectable market, and is the recommended value — an
   * explicit list naming today's markets hides the next one until someone edits
   * this org.
   *
   * `default_country` is the market a workspace gets when none is named,
   * including a self-serve signup on this org's subdomain, which has no way to
   * ask. Both travel together because the default has to be inside the list.
   *
   * Rejected by the API if the list would exclude a workspace the org already
   * has, if it names a market the measurement API cannot compute, or if the
   * default falls outside the list.
   */
  setGeoPolicy: (
    orgId: number,
    allowed_countries: string[] | null,
    default_country: string | null,
  ) =>
    http.put<{
      org_id: number;
      allowed_countries: string[] | null;
      default_country: string | null;
      selectable_countries: string[];
    }>(`/organizations/${orgId}/geo-policy`, { allowed_countries, default_country }),
  /**
   * Set the console language new members of this org start in.
   *
   * Its own call rather than a field on `setFeatures`, because it is not
   * product scope — it is not something the customer bought. Prospective only:
   * the people already in the org keep whatever they have chosen on their
   * account page, and an operator fixing a new customer's default has not
   * asked to overrule them.
   */
  setDefaultLanguage: (orgId: number, default_language: string) =>
    http.put<{
      org_id: number;
      default_language: string;
    }>(`/organizations/${orgId}/default-language`, { default_language }),
  /** Picker-shaped org list; includes the tenant subdomain the paged list omits. */
  options: () => http.get<OrgOption[]>('/users/org-options'),
};

/**
 * The markets a workspace may be pinned to.
 *
 * Served by the backend rather than mirrored here (the way
 * `src/platform/orgFeatures.ts` mirrors the feature keys) because every entry
 * is a value sent to the measurement API as `?country=`: a stale copy would
 * offer an operator a market the API rejects, and the workspace they created
 * would only fail much later, at KPI submission.
 */
export const countryApi = {
  list: () => http.get<{ items: CountryOption[] }>('/countries'),
};

/** The currencies a workspace may be set to (DL-14), served for the same reason. */
export const currencyApi = {
  list: () => http.get<{ items: CurrencyOption[] }>('/currencies'),
};

export const workspaceApi = {
  list: (params: { page?: number; per_page?: number; org_id?: number; q?: string }) =>
    http.get<Paged<Workspace>>('/workspaces', params),
  create: (body: {
    org_id: number;
    name: string;
    /** Required (AD-32): a workspace is pinned to exactly one market. */
    country: string;
    /** Optional default override (DL-14); omit to follow the market's currency. */
    currency?: string | null;
    /** Optional allow-list (DL-14); omit to allow every currency. */
    allowed_currencies?: string[] | null;
    admin_user_id?: number | null;
  }) => http.post<Workspace>('/workspaces', body),
  /** Renames the workspace; the slug stays frozen at its creation value (D13). */
  rename: (workspaceId: number, name: string) =>
    http.put<Pick<Workspace, 'id' | 'name' | 'slug'>>(`/workspaces/${workspaceId}/name`, {
      name,
    }),
  /**
   * Re-pin the workspace to another market.
   *
   * Unlike the slug (frozen at creation, D13) the country is editable, because a
   * wrong one is a measurement error rather than an addressing one. The API
   * refuses the change when the workspace already holds inventory in a
   * different country — those faces have to move first.
   */
  setCountry: (workspaceId: number, country: string) =>
    http.put<Workspace>(`/workspaces/${workspaceId}/country`, { country }),
  /**
   * Set the currencies budgets in this workspace may use and the default,
   * together, because the default must be in the list. `null` default = the
   * market's currency; `null` list = every currency. Saved campaigns keep
   * their own currency.
   */
  setCurrencies: (
    workspaceId: number,
    body: { currency: string | null; allowed_currencies: string[] | null },
  ) => http.put<Workspace>(`/workspaces/${workspaceId}/currencies`, body),
  members: (workspaceId: number) =>
    http.get<Paged<WorkspaceMember>>(`/workspaces/${workspaceId}/members`),
  addMember: (workspaceId: number, user_id: number, workspace_role: WorkspaceRole) =>
    http.post<unknown>(`/workspaces/${workspaceId}/members`, { user_id, workspace_role }),
  updateMemberRole: (workspaceId: number, userId: number, workspace_role: WorkspaceRole) =>
    http.put<unknown>(`/workspaces/${workspaceId}/members/${userId}`, { workspace_role }),
  removeMember: (workspaceId: number, userId: number) =>
    http.del<unknown>(`/workspaces/${workspaceId}/members/${userId}`),
};

export const userApi = {
  list: (params: {
    page?: number;
    per_page?: number;
    q?: string;
    org_id?: number;
    status?: string;
    effective_role?: string;
    sort_by?: string;
    sort_order?: string;
  }) => http.get<Paged<UserRow>>('/users', params),
  get: (userId: number) => http.get<UserDetail>(`/users/${userId}`),
  create: (body: {
    email: string;
    first_name: string;
    last_name: string;
    org_id: number;
    org_role?: string;
    workspace_ids?: number[];
    workspace_role?: WorkspaceRole;
    country_code?: string;
    phone_number?: string;
    send_password_reset?: boolean;
  }) =>
    http.post<{
      user_id: number;
      email: string;
      org_id: number;
      org_role: string;
      workspace_ids: number[];
      password_reset_link_sent: boolean;
    }>('/users', body),
  /** The user's position in their org. Cannot grant staff elevation — see setSeikiStaff. */
  setOrgRole: (userId: number, org_role: string) =>
    http.put<{ home_org_id_missing: boolean; effective_role: string }>(
      `/users/${userId}/org-role`,
      { org_role },
    ),
  /**
   * Seiki staff elevation. Replaces scripts/set_seiki_staff.py. Additive — the
   * response echoes org_role unchanged, because granting this no longer costs
   * the user their org position.
   */
  setSeikiStaff: (userId: number, is_seiki_staff: boolean) =>
    http.put<{ org_role: string | null; effective_role: string }>(
      `/users/${userId}/staff`,
      { is_seiki_staff },
    ),
  /**
   * Move the user to another organization.
   *
   * The repair for "provisioned into the wrong org", which used to mean
   * deleting the account and creating it again — and only worked while the
   * account was still deletable, i.e. while nobody had used it.
   *
   * It takes as much as it gives: memberships in the old org's workspaces,
   * live capability grants and pending requests there, pending invitations the
   * user sent, the legacy owner link, and their refresh tokens. Omit `org_role`
   * to keep the position they hold; omit `workspace_ids` for the destination
   * org's default workspace. `workspace_role` is required — `create` defaults
   * the same field to workspace_admin, and two provisioning calls with opposite
   * defaults is a trap.
   *
   * Silent because the caller renders the refusal in context — a 409 here says
   * which records are keyed to this user, and the answer is a paragraph rather
   * than a toast. `UserDetail.movable` answers it before the click.
   */
  moveToOrg: (
    userId: number,
    body: {
      org_id: number;
      workspace_role: WorkspaceRole;
      org_role?: OrgRole;
      workspace_ids?: number[];
    },
  ) => http.put<UserMoveResult>(`/users/${userId}/organization`, body, { silent: true }),
  setStatus: (userId: number, status: string) =>
    http.put<unknown>(`/users/${userId}/status`, { status }),
  /**
   * Deletes the row, rather than deactivating the account. The only
   * irreversible call in this console, so it is deliberately awkward: the
   * backend rejects it unless `confirm_email` matches the target account, and
   * refuses outright (409) for a user who owns billboards, campaigns, plans or
   * invitations. `UserDetail.deletable` answers that ahead of the click.
   *
   * Silent because the caller renders the refusal in context — a 409 here is a
   * paragraph explaining what to do instead, not a toast.
   */
  remove: (userId: number, confirm_email: string) =>
    http.del<{
      user_id: number;
      email: string;
      name: string;
      sessions_revoked: number;
      workspace_memberships_removed: number;
      capability_grants_removed: number;
      capability_requests_removed: number;
    }>(`/users/${userId}`, { data: { confirm_email }, silent: true }),
  // setAuthorizations is gone with PUT /users/{id}/authorizations. Per-user
  // capabilities are grants now — see capabilityApi.grants / approve.
  sendPasswordReset: (userId: number) =>
    http.post<{ sent: boolean }>(`/users/${userId}/reset-password`),
};

export const tenantApi = {
  list: (params: { page?: number; per_page?: number; q?: string }) =>
    http.get<Paged<Tenant>>('/tenants', params),
  get: (tenantId: number) => http.get<TenantDetail>(`/tenants/${tenantId}`),
  /** Validates a proposed tenants_config/*.json and returns the file to commit. */
  draftConfig: (body: Record<string, unknown>) =>
    http.post<TenantConfigDraft>('/tenants/config-draft', body),
};


export const capabilityApi = {
  /** The review queue. Pending sorts first server-side, regardless of age. */
  requests: (params: { status?: string; page?: number; per_page?: number }) =>
    http.get<Paged<CapabilityRequest>>('/capabilities/requests', params),

  /**
   * Approve, creating the grant. `expires_at` is optional but usually the right
   * call: a dated grant is the thing the old per-user boolean could never do.
   */
  approve: (requestId: number, body: { expires_at?: string; decision_note?: string }) =>
    http.post<{ grant_id: number; capability: string; expires_at: string | null }>(
      `/capabilities/requests/${requestId}/approve`,
      body,
    ),

  /** Decline. The note is shown to the requester, so it is worth writing. */
  decline: (requestId: number, decision_note?: string) =>
    http.post<{ id: number; status: string }>(
      `/capabilities/requests/${requestId}/decline`,
      { decision_note },
    ),

  grants: (params: { include_revoked?: boolean; user_id?: number; page?: number; per_page?: number }) =>
    http.get<Paged<CapabilityGrant>>('/capabilities/grants', params),

  /** Grant without a request, for staff acting on a conversation elsewhere. */
  createGrant: (body: {
    user_id: number;
    workspace_id: number;
    capability: string;
    expires_at?: string;
    reason?: string;
  }) => http.post<{ grant_id: number }>('/capabilities/grants', body),

  /** Soft revoke — the audit trail outlives the grant. */
  revokeGrant: (grantId: number) =>
    http.del<{ grant_id: number }>(`/capabilities/grants/${grantId}`),

  /** The whitelist, so this console never hard-codes which capabilities exist. */
  grantable: () => http.get<{ capabilities: string[] }>('/capabilities/grantable'),
};
